/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-15 11:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { Modal, FormLayout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import CustomDropZone from 'src/components/views/common/custom-dropzone';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import { isRequired, isEmail, isEmpty } from 'src/helpers/form-validation-helper';
import { customizeSelect, requestCustomization } from 'src/constants/feedback-options';
import { prettyText } from 'src/helpers';

import * as suportTicketActions from 'actions/support-ticket-actions';
import * as themeSetupAction from 'actions/theme-setup-actions';

const isRequiredValue = isRequired();
const isEmailValue = isEmail();

type FeedbackType = {
  email: string,
  content: string,
  selection: { code: string, codeDescription: string }
};

type Props = {
  success: boolean,
  loading: boolean,
  reset: () => void,
  resetSendSupportTicket: () => void,
  handleChangeModal: () => void,
  activeSendRequestModal: () => void,
  handleSubmit: Function => void,
  sendSupportTicket: (feedback: FeedbackType, files: string[]) => void,
  pristine: boolean,
  submitting: boolean
};

type ComposedProps = Props & WithI18nProps;

class SendSupportTicket extends Component<ComposedProps> {
  state = {
    displayThemeList: false,
    displayRequestCustomization: false,
    themeList: [],
    selectedFiles: []
  };

  componentDidMount() {
    // Get themes
    this.props.getThemes();
  }
  componentWillReceiveProps(nextProps) {
    if (nextProps.success === true && nextProps.activeSendRequestModal === true) {
      // Reset form
      this.props.reset();

      // Reset all states
      this.props.resetSendSupportTicket();

      // Close modal
      this.props.handleChangeModal();
    }

    if (!nextProps.getThemesLoading && this.props.getThemesLoading) {
      this.buildThemeList(nextProps.themes);
    }

    if (nextProps.loading === true && this.props.loading === false) {
      startSABLoading();
    }

    if (nextProps.loading === false && this.props.loading === true) {
      stopSABLoading();
    }
  }

  render() {
    const {
      activeSendRequestModal,
      handleChangeModal,
      loading,
      handleSubmit,
      pristine,
      submitting,
      i18n
    } = this.props;
    const { displayThemeList, displayRequestCustomization, themeList } = this.state;

    const formattedCustomizeSelect = customizeSelect.map(function (option) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });

    const formattedRequestCustomization = requestCustomization.map(function (option) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });

    return (
      <Modal
        open={activeSendRequestModal}
        onClose={handleChangeModal}
        title={i18n.translate('AdminHTML.Dashboard.Common.sendASupportTicket')}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.send'),
          disabled: pristine || submitting,
          loading: loading && !pristine,
          onAction: handleSubmit(this.handleSave.bind(this))
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: handleChangeModal
          }
        ]}>
        <Modal.Section>
          <form className="BC-SupportTicket__Form" onSubmit={this.handleDefaultSubmit}>
            <FormLayout>
              <Field
                name="name"
                label={i18n.translate('AdminHTML.Common.FieldLabel.yourName')}
                placeholder={i18n.translate('AdminHTML.Common.Placeholder.name')}
                component={RenderTextField}
              />
              <Field
                name="email"
                label={i18n.translate('AdminHTML.Common.FieldLabel.yourEmail')}
                placeholder={i18n.translate('AdminHTML.Common.Placeholder.email')}
                component={RenderTextField}
                validate={[isRequiredValue, isEmailValue]}
              />

              <Field
                name="storeId"
                label={i18n.translate(
                  'AdminHTML.Dashboard.HelpCenter.sendSupportTicket_FieldLabel_StoreAddress'
                )}
                placeholder={i18n.translate(
                  'AdminHTML.Dashboard.HelpCenter.sendSupportTicket_FieldPlaceholder_StoreAddress'
                )}
                component={RenderTextField}
                validate={[isRequiredValue]}
              />

              <Field
                name="selection"
                label={i18n.translate(
                  'AdminHTML.Dashboard.HelpCenter.sendSupportTicket_FieldLabel_CustomRequestType'
                )}
                options={formattedCustomizeSelect}
                component={RenderSelect}
                onChange={this.onChangeRequest}
              />

              {displayRequestCustomization && (
                <Field
                  name="custom"
                  label={i18n.translate(
                    'AdminHTML.Dashboard.HelpCenter.sendSupportTicket_FieldLabel_CustomizationsRegarding'
                  )}
                  options={formattedRequestCustomization}
                  component={RenderSelect}
                />
              )}

              {displayThemeList && (
                <Field
                  name="theme"
                  label={i18n.translate(
                    'AdminHTML.Dashboard.HelpCenter.sendSupportTicket_FieldLabel_YourTheme'
                  )}
                  options={themeList}
                  component={RenderSelect}
                />
              )}

              {/* Choose file*/}
              <CustomDropZone
                allowMultiple={true}
                onSelectFile={this.onSelectFile}
                showDropzone={true}
                accept={
                  'image/*,application/pdf,text/csv,video/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                }
                fileTypes={['image/png', 'image/jpeg']}
                tooltip={i18n.translate(
                  'AdminHTML.Dashboard.HelpCenter.sendSupportTicket_AttachFiles_Tooltip'
                )}
              />

              <Field
                name="content"
                label={i18n.translate(
                  'AdminHTML.Dashboard.HelpCenter.sendSupportTicket_FieldLabel_YourMessage'
                )}
                placeholder={i18n.translate(
                  'AdminHTML.Dashboard.HelpCenter.sendSupportTicket_FieldPlaceholder_YourMessage'
                )}
                component={RenderTextField}
                anotherProps={{ multiline: 5 }}
              />
            </FormLayout>
          </form>
        </Modal.Section>
      </Modal>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  // Validate value
  validateSubmit = data => {
    const { i18n } = this.props;
    if (data.selection && data.selection !== '') {
      if (data.selection === 'others' && isEmpty(data.content)) {
        throw new SubmissionError({
          content: i18n.translate('AdminHTML.Dashboard.HelpCenter.sendSupportTicket_Error1')
        });
      }
    } else {
      throw new SubmissionError({
        selection: i18n.translate('AdminHTML.Dashboard.HelpCenter.sendSupportTicket_Error2')
      });
    }

    if (
      ['appIssue', 'incorrectData', 'appRequest'].includes(data.selection) &&
      isEmpty(data.theme)
    ) {
      throw new SubmissionError({
        theme: i18n.translate('AdminHTML.Dashboard.HelpCenter.sendSupportTicket_Error3')
      });
    }

    if (data.selection === 'appRequest' && !data.custom) {
      throw new SubmissionError({
        custom: i18n.translate('AdminHTML.Dashboard.HelpCenter.sendSupportTicket_Error4')
      });
    }

    return true;
  };

  //Handle submit
  handleSave = data => {
    if (this.validateSubmit(data)) {
      const { themeList = [], selectedFiles = [] } = this.state;
      const { sendSupportTicket, i18n } = this.props;
      const { name, email, selection, content, custom, theme: themeId } = _.pick(data, [
        'name',
        'email',
        'selection',
        'content',
        'custom',
        'theme'
      ]);
      const formattedRequestCustomization = requestCustomization.map(function (option) {
        return {
          ...option,
          label: i18n.translate(option.label)
        };
      });

      const option = _.find(customizeSelect, { value: selection });
      const customization = _.find(formattedRequestCustomization, { value: custom });
      const theme = _.find(themeList, { value: themeId });

      let requestCustom = _.get(customization, 'label', '');

      const feedback = {
        name: prettyText(name),
        email: prettyText(email),
        content: prettyText(content),
        customization: requestCustom,
        theme: theme ? { id: theme.value, name: theme.label } : {},
        selection: { code: option.value, codeDescription: i18n.translate(option.label) }
      };

      sendSupportTicket(feedback, selectedFiles);

      // Reset selected files
      this.setState({ selectedFiles: [] });
    }
  };

  onChangeRequest = selected => {
    let updateState = { displayThemeList: false, displayRequestCustomization: false };
    if (['appIssue', 'incorrectData', 'appRequest'].includes(selected)) {
      updateState.displayThemeList = true;
    }

    if (selected === 'appRequest') {
      updateState.displayRequestCustomization = true;
    }

    this.setState(updateState);
  };

  buildThemeList = themes => {
    const { i18n } = this.props;
    const themeList = themes.map(theme => {
      const { id, name, published } = theme;
      return {
        label: published ? `${name} - Main` : name,
        value: id.toString()
      };
    });

    themeList.splice(0, 0, {
      label: i18n.translate('AdminHTML.Dashboard.HelpCenter.buildThemeList_Label'),
      value: ''
    });

    this.setState({ themeList });
  };

  // Handle on select file from dropzone
  onSelectFile = selectedFiles => {
    this.setState({ selectedFiles });
  };
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    loading: state.supportTicket.sendSupportTicketLoading,
    success: state.supportTicket.sendSupportTicketSuccess,
    error: state.supportTicket.error,
    themes: _.get(state.themeSetup, 'themes', []),
    getThemesLoading: _.get(state.themeSetup, 'getThemesLoading', false),
    initialValues: { storeId: tenantApp.tenantId }
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendSupportTicket: (body: Object, files: string[]) =>
    dispatch(suportTicketActions.sendSupportTicket(body, files, ownProps.i18n)),
  resetSendSupportTicket: () => dispatch(suportTicketActions.resetSendSupportTicket()),
  getThemes: () => dispatch(themeSetupAction.getThemes(ownProps.i18n, true))
});

const SendSupportTicketForm = reduxForm({
  form: 'SendSupportTicket', // a unique identifier for this form
  enableReinitialize: true
})(SendSupportTicket);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SendSupportTicketForm));
